import React from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  List,
  ListItem,
  Divider,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import WarningIcon from "@mui/icons-material/Warning";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

import { useNotifications } from "../../context/NotificationContext";
import type { Notif } from "../../context/NotificationContext";

/* ---------- Helpers ---------- */
const isToday = (date: Date) =>
  new Date().toDateString() === date.toDateString();

const isYesterday = (date: Date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toDateString() === date.toDateString();
};

const formatTime = (iso: string) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
  return `${Math.floor(diff / 86400)} days ago`;
};

const getSectionIcon = (title: string) => {
  switch (title) {
    case "Unread":
      return { icon: <WarningIcon />, color: "#EF4444" };
    case "Today":
      return { icon: <SystemUpdateAltIcon />, color: "#3B82F6" };
    case "Yesterday":
      return { icon: <SystemUpdateAltIcon />, color: "#64748B" };
    case "Earlier":
      return { icon: <SystemUpdateAltIcon />, color: "#475569" };
    default:
      return null;
  }
};

/* ---------- Component ---------- */
const NotificationsPage: React.FC = () => {
  const { notifications, markAllAsRead, clearAll, markOneAsRead } =
    useNotifications();

  /* ---------- Grouping ---------- */
  const { unread, today, yesterday, earlier } = React.useMemo(() => {
    const unread = notifications.filter((n) => !n.read);

    const today = notifications.filter(
      (n) => n.read && isToday(new Date(n.time))
    );

    const yesterday = notifications.filter(
      (n) => n.read && isYesterday(new Date(n.time))
    );

    const earlier = notifications.filter(
      (n) =>
        n.read &&
        !isToday(new Date(n.time)) &&
        !isYesterday(new Date(n.time))
    );

    return { unread, today, yesterday, earlier };
  }, [notifications]);

  const renderSection = (title: string, items: Notif[]) => {
    if (items.length === 0) return null;

    const sectionIcon = getSectionIcon(title);

    return (
      <Box sx={{ mb: 4 }}>
        {/* Section Header */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
          {sectionIcon && (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: `${sectionIcon.color}22`,
                color: sectionIcon.color,
              }}
            >
              {sectionIcon.icon}
            </Box>
          )}

          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#9CA3AF",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {title}
          </Typography>
        </Stack>

        <List disablePadding>
          {items.map((notif, i) => (
            <React.Fragment key={notif.id}>
              <ListItem
                component="button"
                role="button"
                onClick={() => {
                  if (!notif.read) markOneAsRead(notif.id); // ✅ number
                }}
                sx={{
                  px: 0,
                  py: 1.6,
                  pl: 1,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "background 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(49,121,153,0.12)",
                  },
                }}
              >
                <Box>
                  <Typography
                    fontSize={15}
                    fontWeight={notif.read ? 500 : 600}
                    color="#E5E7EB"
                  >
                    {notif.message}
                  </Typography>
                  <Typography
                    fontSize={12}
                    color="rgba(255,255,255,0.6)"
                  >
                    {formatTime(notif.time)}
                  </Typography>
                </Box>
              </ListItem>

              {i !== items.length - 1 && (
                <Divider
                  sx={{
                    borderColor: "rgba(255,255,255,0.06)",
                    ml: 1,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#273142",
        color: "#E5E7EB",
        px: { xs: 2, md: 6 },
        py: { xs: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography fontSize={22} fontWeight={700}>
          Notifications
        </Typography>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Mark all as read">
            <IconButton onClick={markAllAsRead}>
              <DoneAllIcon sx={{ color: "#317999" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear all">
            <IconButton onClick={clearAll}>
              <CloseIcon sx={{ color: "#317999" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Content */}
      {notifications.length === 0 ? (
        <Typography
          textAlign="center"
          color="rgba(255,255,255,0.6)"
          fontSize={15}
          mt={10}
        >
          You're all caught up
        </Typography>
      ) : (
        <>
          {renderSection("Unread", unread)}
          {renderSection("Today", today)}
          {renderSection("Yesterday", yesterday)}
          {renderSection("Earlier", earlier)}
        </>
      )}
    </Box>
  );
};

export default NotificationsPage;
